import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { charactersApi, aiApi } from '../../lib/api';
import { 
    saveImageToCharacter as saveToStorage, 
    getCharacterImages, 
    setDefaultImage as setDefaultInStorage,
    deleteImage 
} from '../../lib/imageStorage';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
    Plus, Users, Sparkles, Save, Trash2, Wand2, Send, X,
    Image, Download, Loader2, Check, Copy, Edit2, ChevronLeft, Star
} from 'lucide-react';

// Character Card Component with expandable details and AI assistant
const CharacterCard = ({ character, bookId, onUpdate, onDelete }) => {
    const { user, refreshUser } = useAuth();
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editData, setEditData] = useState(character);
    const [showAI, setShowAI] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiSuggestion, setAiSuggestion] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [showImages, setShowImages] = useState(false);
    const [characterImages, setCharacterImages] = useState([]);
    const [defaultImageIndex, setDefaultImageIndex] = useState(0);

    // Load character images from shared storage
    useEffect(() => {
        const data = getCharacterImages(bookId, character.id);
        setCharacterImages(data.images || []);
        setDefaultImageIndex(data.defaultIndex || 0);
    }, [character.id, bookId]);

    // Refresh images when dialog opens
    useEffect(() => {
        if (showImages) {
            const data = getCharacterImages(bookId, character.id);
            setCharacterImages(data.images || []);
            setDefaultImageIndex(data.defaultIndex || 0);
        }
    }, [showImages, character.id, bookId]);

    const handleSave = async () => {
        try {
            await charactersApi.update(character.id, editData);
            onUpdate({ ...character, ...editData });
            setEditing(false);
            toast.success('Character updated');
        } catch (error) {
            toast.error('Failed to update character');
        }
    };

    const handleAIFleshOut = async () => {
        if (!aiPrompt.trim()) return;
        setAiLoading(true);
        setAiSuggestion(null);

        try {
            const res = await aiApi.write({
                prompt: `You are a character development assistant. Help flesh out this character:
                
Name: ${character.name}
Role: ${character.role}
Current Description: ${character.description || 'None'}
Current Backstory: ${character.backstory || 'None'}
Current Goals: ${character.goals || 'None'}
Current Flaws: ${character.flaws || 'None'}

Writer's Request: ${aiPrompt}

Provide detailed, creative suggestions organized by section. Format as:
**Description:** [suggestion]
**Backstory:** [suggestion]
**Goals:** [suggestion]
**Flaws:** [suggestion]
**Character Arc:** [suggestion]
**Relationships:** [potential relationships]
**Quirks/Habits:** [unique traits]`,
                context: `Book ID: ${bookId}, Character development`,
                assist_type: 'story_help'
            });

            setAiSuggestion({
                text: res.data.response || res.data.suggestion,
                sections: parseAISections(res.data.response || res.data.suggestion)
            });
            refreshUser();
        } catch (error) {
            toast.error('AI request failed');
        } finally {
            setAiLoading(false);
        }
    };

    const parseAISections = (text) => {
        const sections = {};
        const patterns = [
            { key: 'description', pattern: /\*\*Description:\*\*\s*([\s\S]*?)(?=\*\*|$)/i },
            { key: 'backstory', pattern: /\*\*Backstory:\*\*\s*([\s\S]*?)(?=\*\*|$)/i },
            { key: 'goals', pattern: /\*\*Goals:\*\*\s*([\s\S]*?)(?=\*\*|$)/i },
            { key: 'flaws', pattern: /\*\*Flaws:\*\*\s*([\s\S]*?)(?=\*\*|$)/i },
            { key: 'arc', pattern: /\*\*Character Arc:\*\*\s*([\s\S]*?)(?=\*\*|$)/i },
            { key: 'relationships', pattern: /\*\*Relationships:\*\*\s*([\s\S]*?)(?=\*\*|$)/i },
            { key: 'quirks', pattern: /\*\*Quirks\/Habits:\*\*\s*([\s\S]*?)(?=\*\*|$)/i }
        ];
        
        patterns.forEach(({ key, pattern }) => {
            const match = text.match(pattern);
            if (match) sections[key] = match[1].trim();
        });
        
        return sections;
    };

    const acceptSection = (section, value) => {
        setEditData(prev => ({ ...prev, [section]: value }));
        toast.success(`${section} accepted - save to apply`);
        setEditing(true);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const setAsDefault = (index) => {
        setDefaultInStorage(bookId, character.id, index);
        setDefaultImageIndex(index);
        toast.success('Default image updated');
    };

    const removeImage = (index) => {
        const imageToDelete = characterImages[index];
        if (imageToDelete) {
            deleteImage(bookId, character.id, imageToDelete.id);
            // Refresh from storage
            const data = getCharacterImages(bookId, character.id);
            setCharacterImages(data.images || []);
            setDefaultImageIndex(data.defaultIndex || 0);
            toast.success('Image moved to trash (recoverable for 10 days)');
        }
    };

    const defaultImage = characterImages[defaultImageIndex];

    return (
        <div className="card-default overflow-hidden" data-testid={`character-${character.id}`}>
            {/* Card Header */}
            <div className="p-4 border-b border-border/50">
                <div className="flex items-start gap-3">
                    {/* Character Image */}
                    <div 
                        className="relative w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0 cursor-pointer group"
                        onClick={() => setShowImages(true)}
                    >
                        {defaultImage ? (
                            <img src={defaultImage.url} alt={character.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <Users className="h-8 w-8 text-muted-foreground/50" />
                            </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Image className="h-5 w-5 text-white" />
                        </div>
                        {characterImages.length > 1 && (
                            <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1 rounded">
                                +{characterImages.length - 1}
                            </div>
                        )}
                    </div>
                    
                    {/* Character Info */}
                    <div className="flex-1 min-w-0">
                        <h3 className="font-serif font-semibold text-lg truncate">{character.name}</h3>
                        <Badge variant="outline" className="mt-1">{character.role}</Badge>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1">
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => setShowAI(!showAI)}
                            title="AI Assistant"
                        >
                            <Wand2 className={`h-4 w-4 ${showAI ? 'text-accent' : ''}`} />
                        </Button>
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => setExpanded(!expanded)}
                        >
                            <ChevronLeft className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : '-rotate-90'}`} />
                        </Button>
                    </div>
                </div>
                
                {/* Collapsed Preview */}
                {!expanded && character.description && (
                    <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{character.description}</p>
                )}
            </div>

            {/* Expanded Content */}
            {expanded && (
                <div className="p-4 space-y-4">
                    {editing ? (
                        /* Edit Mode */
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs">Description</Label>
                                <Textarea 
                                    value={editData.description || ''} 
                                    onChange={(e) => setEditData({...editData, description: e.target.value})}
                                    rows={3}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Backstory</Label>
                                <Textarea 
                                    value={editData.backstory || ''} 
                                    onChange={(e) => setEditData({...editData, backstory: e.target.value})}
                                    rows={3}
                                    className="mt-1"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Goals</Label>
                                    <Input 
                                        value={editData.goals || ''} 
                                        onChange={(e) => setEditData({...editData, goals: e.target.value})}
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Flaws</Label>
                                    <Input 
                                        value={editData.flaws || ''} 
                                        onChange={(e) => setEditData({...editData, flaws: e.target.value})}
                                        className="mt-1"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Character Arc</Label>
                                <Textarea 
                                    value={editData.arc || ''} 
                                    onChange={(e) => setEditData({...editData, arc: e.target.value})}
                                    rows={2}
                                    className="mt-1"
                                />
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditData(character); }}>
                                    Cancel
                                </Button>
                                <Button size="sm" onClick={handleSave}>
                                    <Save className="h-3 w-3 mr-1" /> Save
                                </Button>
                            </div>
                        </div>
                    ) : (
                        /* View Mode */
                        <div className="space-y-3">
                            {character.description && (
                                <div>
                                    <span className="label-text text-accent text-xs">Description</span>
                                    <p className="text-sm mt-1">{character.description}</p>
                                </div>
                            )}
                            {character.backstory && (
                                <div>
                                    <span className="label-text text-accent text-xs">Backstory</span>
                                    <p className="text-sm mt-1">{character.backstory}</p>
                                </div>
                            )}
                            {character.goals && (
                                <div>
                                    <span className="label-text text-accent text-xs">Goals</span>
                                    <p className="text-sm mt-1">{character.goals}</p>
                                </div>
                            )}
                            {character.flaws && (
                                <div>
                                    <span className="label-text text-accent text-xs">Flaws</span>
                                    <p className="text-sm mt-1">{character.flaws}</p>
                                </div>
                            )}
                            {character.arc && (
                                <div>
                                    <span className="label-text text-accent text-xs">Character Arc</span>
                                    <p className="text-sm mt-1">{character.arc}</p>
                                </div>
                            )}
                            <div className="flex justify-end gap-2 pt-2">
                                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                                    <Edit2 className="h-3 w-3 mr-1" /> Edit
                                </Button>
                                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(character.id)}>
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* AI Assistant Panel */}
            {showAI && (
                <div className="p-4 bg-accent/5 border-t border-accent/20">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="h-4 w-4 text-accent" />
                        <span className="font-semibold text-sm">AI Character Assistant</span>
                        <span className="text-xs text-muted-foreground ml-auto">{user?.credits || 0} credits</span>
                    </div>
                    
                    <div className="flex gap-2 mb-3">
                        <Input
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            placeholder="e.g., 'Give this character a tragic backstory' or 'What motivates them?'"
                            className="flex-1"
                            onKeyDown={(e) => e.key === 'Enter' && handleAIFleshOut()}
                        />
                        <Button onClick={handleAIFleshOut} disabled={aiLoading || !aiPrompt.trim()} size="sm">
                            {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                    </div>

                    {/* AI Suggestion */}
                    {aiSuggestion && (
                        <div className="space-y-3 mt-4">
                            <div className="text-xs font-medium text-muted-foreground">AI Suggestions (click to accept):</div>
                            {Object.entries(aiSuggestion.sections).map(([key, value]) => (
                                <div key={key} className="p-3 bg-background rounded-lg border">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-semibold capitalize text-accent">{key}</span>
                                        <div className="flex gap-1">
                                            <Button 
                                                size="sm" 
                                                variant="ghost" 
                                                className="h-6 px-2"
                                                onClick={() => copyToClipboard(value)}
                                            >
                                                <Copy className="h-3 w-3" />
                                            </Button>
                                            {['description', 'backstory', 'goals', 'flaws', 'arc'].includes(key) && (
                                                <Button 
                                                    size="sm" 
                                                    variant="ghost" 
                                                    className="h-6 px-2 text-success"
                                                    onClick={() => acceptSection(key, value)}
                                                >
                                                    <Check className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm text-muted-foreground">{value}</p>
                                </div>
                            ))}
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="w-full"
                                onClick={() => setAiSuggestion(null)}
                            >
                                <X className="h-3 w-3 mr-1" /> Clear Suggestions
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Character Images Dialog */}
            <Dialog open={showImages} onOpenChange={setShowImages}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-serif">{character.name} - Character Images</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {/* Images Grid */}
                        {characterImages.length > 0 ? (
                            <div className="grid grid-cols-3 gap-3">
                                {characterImages.map((img, index) => (
                                    <div 
                                        key={img.id} 
                                        className={`relative group rounded-lg overflow-hidden border-2 ${index === defaultImageIndex ? 'border-accent' : 'border-transparent'}`}
                                    >
                                        <div className="aspect-square">
                                            <img src={img.url} alt={img.title} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                                            {index !== defaultImageIndex && (
                                                <Button size="sm" variant="secondary" onClick={() => setAsDefault(index)}>
                                                    Set Default
                                                </Button>
                                            )}
                                            <Button size="sm" variant="secondary" asChild>
                                                <a href={img.url} download={`${img.title}.png`} target="_blank" rel="noopener noreferrer">
                                                    <Download className="h-3 w-3 mr-1" /> Download
                                                </a>
                                            </Button>
                                            <Button size="sm" variant="destructive" onClick={() => removeImage(index)}>
                                                <Trash2 className="h-3 w-3" />
                                            </Button>
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-2">
                                            <p className="text-xs text-white truncate">{img.title}</p>
                                            {index === defaultImageIndex && (
                                                <Badge className="mt-1 text-xs">Default</Badge>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <Image className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                                <p className="text-sm text-muted-foreground">No character images yet</p>
                            </div>
                        )}

                        {/* Instructions */}
                        <div className="p-3 bg-muted/50 rounded-lg text-sm">
                            <p className="text-muted-foreground">
                                Generate images using the <strong>Character Illustrations</strong> section below, 
                                or from the <strong>Illustrations tab</strong>. Images will be saved here automatically 
                                when you include "{character.name}" in your prompt.
                            </p>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Main Characters Panel Component
const CharactersPanel = ({ bookId, characters, setCharacters }) => {
    const { user, refreshUser } = useAuth();
    const [showNew, setShowNew] = useState(false);
    const [newChar, setNewChar] = useState({
        name: '', role: 'supporting', description: '', backstory: '', goals: '', flaws: '', arc: ''
    });
    
    // Character Illustrations AI
    const [illustrationPrompt, setIllustrationPrompt] = useState('');
    const [selectedCharacter, setSelectedCharacter] = useState('');
    const [imageTitle, setImageTitle] = useState('');
    const [illustrationLoading, setIllustrationLoading] = useState(false);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [illustrationTier, setIllustrationTier] = useState('standard');

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const response = await charactersApi.create({ ...newChar, book_id: bookId });
            setCharacters([...characters, response.data]);
            setShowNew(false);
            setNewChar({ name: '', role: 'supporting', description: '', backstory: '', goals: '', flaws: '', arc: '' });
            toast.success('Character created');
        } catch (error) {
            toast.error('Failed to create character');
        }
    };

    const handleUpdateCharacter = (updatedChar) => {
        setCharacters(prev => prev.map(c => c.id === updatedChar.id ? updatedChar : c));
    };

    const handleDeleteCharacter = async (charId) => {
        try {
            await charactersApi.delete(charId);
            setCharacters(prev => prev.filter(c => c.id !== charId));
            toast.success('Character deleted');
        } catch (error) {
            toast.error('Failed to delete character');
        }
    };

    const handleGenerateIllustration = async () => {
        if (!illustrationPrompt.trim() || !selectedCharacter) {
            toast.error('Please select a character and enter a description');
            return;
        }
        
        setIllustrationLoading(true);
        setGeneratedImage(null);
        
        const character = characters.find(c => c.id === selectedCharacter);
        const fullPrompt = `${character?.name}: ${illustrationPrompt}, character portrait, book illustration style`;
        
        try {
            const res = await aiApi.illustrate({
                prompt: fullPrompt,
                tier: illustrationTier,
                book_id: bookId
            });
            
            setGeneratedImage({
                url: res.data.image_url,
                title: imageTitle || `${character?.name} - ${illustrationPrompt.slice(0, 30)}`,
                characterId: selectedCharacter,
                characterName: character?.name
            });
            
            refreshUser();
            toast.success('Character illustration generated!');
        } catch (error) {
            toast.error('Failed to generate illustration');
        } finally {
            setIllustrationLoading(false);
        }
    };

    const handleSaveImageToCharacter = (setAsDefault = false) => {
        if (!generatedImage) return;
        
        // Save using shared storage utility
        saveToStorage(bookId, generatedImage.characterId, generatedImage.characterName, {
            url: generatedImage.url,
            title: generatedImage.title
        }, setAsDefault);
        
        toast.success(`Image saved to ${generatedImage.characterName}'s gallery${setAsDefault ? ' (set as default)' : ''}`);
        
        // Reset
        setGeneratedImage(null);
        setIllustrationPrompt('');
        setImageTitle('');
    };

    return (
        <div className="p-8">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-serif text-2xl font-semibold">Characters</h2>
                    <p className="text-muted-foreground mt-1">Create and develop your story's characters</p>
                </div>
                <Dialog open={showNew} onOpenChange={setShowNew}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-character-btn">
                            <Plus className="h-4 w-4 mr-2" /> New Character
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle>New Character</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreate} className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Name</Label>
                                    <Input value={newChar.name} onChange={(e) => setNewChar({...newChar, name: e.target.value})} required />
                                </div>
                                <div className="space-y-2">
                                    <Label>Role</Label>
                                    <select 
                                        className="w-full h-10 rounded-md border border-input bg-background px-3"
                                        value={newChar.role}
                                        onChange={(e) => setNewChar({...newChar, role: e.target.value})}
                                    >
                                        <option value="protagonist">Protagonist</option>
                                        <option value="antagonist">Antagonist</option>
                                        <option value="supporting">Supporting</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Textarea value={newChar.description} onChange={(e) => setNewChar({...newChar, description: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                                <Label>Goals</Label>
                                <Input value={newChar.goals} onChange={(e) => setNewChar({...newChar, goals: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                                <Label>Flaws</Label>
                                <Input value={newChar.flaws} onChange={(e) => setNewChar({...newChar, flaws: e.target.value})} />
                            </div>
                            <div className="flex justify-end gap-3">
                                <Button type="button" variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                                <Button type="submit">Create Character</Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Character Cards Grid */}
            {characters.length === 0 ? (
                <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <h3 className="font-serif font-semibold mb-1">No characters yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Create characters to bring your story to life
                    </p>
                    <Button onClick={() => setShowNew(true)}>
                        <Plus className="h-4 w-4 mr-2" /> Add Your First Character
                    </Button>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
                    {characters.map((char) => (
                        <CharacterCard 
                            key={char.id} 
                            character={char} 
                            bookId={bookId}
                            onUpdate={handleUpdateCharacter}
                            onDelete={handleDeleteCharacter}
                        />
                    ))}
                </div>
            )}

            {/* Character Illustrations Section */}
            {characters.length > 0 && (
                <div className="card-default p-6 mt-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg bg-accent/10">
                            <Image className="h-5 w-5 text-accent" />
                        </div>
                        <div>
                            <h3 className="font-semibold">Character Illustrations</h3>
                            <p className="text-sm text-muted-foreground">Generate visual representations of your characters</p>
                        </div>
                        <div className="ml-auto text-sm text-muted-foreground">
                            {user?.credits || 0} credits
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Generation Form */}
                        <div className="space-y-4">
                            <div>
                                <Label>Select Character</Label>
                                <select
                                    className="w-full h-10 rounded-md border border-input bg-background px-3 mt-1"
                                    value={selectedCharacter}
                                    onChange={(e) => setSelectedCharacter(e.target.value)}
                                    data-testid="char-illustration-select"
                                >
                                    <option value="">Choose a character...</option>
                                    {characters.map(char => (
                                        <option key={char.id} value={char.id}>{char.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <Label>Image Title</Label>
                                <Input
                                    value={imageTitle}
                                    onChange={(e) => setImageTitle(e.target.value)}
                                    placeholder="e.g., 'Happy King Tater' or 'General King Tater'"
                                    className="mt-1"
                                />
                            </div>

                            <div>
                                <Label>Description / Mood / Pose</Label>
                                <Textarea
                                    value={illustrationPrompt}
                                    onChange={(e) => setIllustrationPrompt(e.target.value)}
                                    placeholder="e.g., 'wearing royal armor, standing heroically, determined expression'"
                                    rows={2}
                                    className="mt-1"
                                    data-testid="char-illustration-prompt"
                                />
                            </div>

                            <div>
                                <Label>Quality</Label>
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                    <button
                                        className={`p-2 rounded-lg border text-sm ${illustrationTier === 'standard' ? 'border-accent bg-accent/10' : 'border-border'}`}
                                        onClick={() => setIllustrationTier('standard')}
                                    >
                                        Standard (5 credits)
                                    </button>
                                    <button
                                        className={`p-2 rounded-lg border text-sm ${illustrationTier === 'premium' ? 'border-accent bg-accent/10' : 'border-border'}`}
                                        onClick={() => setIllustrationTier('premium')}
                                    >
                                        Premium (10 credits)
                                    </button>
                                </div>
                            </div>

                            <Button 
                                onClick={handleGenerateIllustration} 
                                className="w-full"
                                disabled={illustrationLoading || !selectedCharacter || !illustrationPrompt.trim()}
                                data-testid="generate-char-illustration-btn"
                            >
                                {illustrationLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        Generate Character Image
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Preview */}
                        <div>
                            {generatedImage ? (
                                <div className="space-y-3">
                                    <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                                        <img 
                                            src={generatedImage.url} 
                                            alt={generatedImage.title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="p-3 bg-muted/50 rounded-lg">
                                        <p className="font-medium text-sm">{generatedImage.title}</p>
                                        <p className="text-xs text-muted-foreground">for {generatedImage.characterName}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex gap-2">
                                            <Button onClick={() => handleSaveImageToCharacter(false)} className="flex-1">
                                                <Save className="h-4 w-4 mr-2" />
                                                Save to Gallery
                                            </Button>
                                            <Button variant="outline" asChild>
                                                <a href={generatedImage.url} download={`${generatedImage.title}.png`} target="_blank" rel="noopener noreferrer">
                                                    <Download className="h-4 w-4" />
                                                </a>
                                            </Button>
                                        </div>
                                        <Button 
                                            onClick={() => handleSaveImageToCharacter(true)} 
                                            variant="secondary"
                                            className="w-full"
                                        >
                                            <Star className="h-4 w-4 mr-2" />
                                            Save as Default Portrait
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="aspect-square rounded-lg bg-muted/50 flex items-center justify-center">
                                    <div className="text-center">
                                        <Image className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                                        <p className="text-sm text-muted-foreground">
                                            Generated image will appear here
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tips */}
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                        <strong>Tips:</strong> Each character can have multiple images with different titles (e.g., "Happy King Tater", "General King Tater"). 
                        Click on a character's image to manage their gallery and set a default portrait.
                    </div>
                </div>
            )}
        </div>
    );
};

export { CharacterCard };
export default CharactersPanel;
